import { createSelector } from 'reselect';

import type { State } from '../../Types';
import { LoadStatus } from '../../Types';
import type { PaymentsOccupant, PaymentsProperty } from './Types/PaymentsProperty';

const paymentsState = ({ payments }: State) => payments;

// Properties
const propertiesSlice = createSelector(paymentsState, ({ properties }) => properties);

export const propertiesLoadStatus = createSelector(propertiesSlice, ({ loadStatus }) => loadStatus);

export const propertiesAreLoaded = createSelector(propertiesLoadStatus, (loadStatus) => loadStatus === LoadStatus.LOADED);

export const properties = createSelector(propertiesSlice, ({ loadStatus, ...rest }) => rest);

export const propertiesList = createSelector(propertiesSlice, ({ loadStatus, errorState, sortOrder, ...rest }) =>
    Object.values(rest),
);

export const sortedPropertiesList = createSelector(
    paymentsState,
    ({ properties: propertiesState }): PaymentsProperty[] => propertiesState.sortOrder?.map((i) => propertiesState[i]) || [],
);

export const propertiesWithOccupants = createSelector(sortedPropertiesList, (propertiesListState): PaymentsProperty[] =>
    propertiesListState.filter((property) => property.occupants?.length > 0),
);

export const occupantsFromProperties = createSelector(propertiesWithOccupants, (propertiesListState) =>
    propertiesListState.flatMap((property) =>
        property.occupants.map((occupant) => ({
            id: occupant.id,
            name: occupant.name,
            propertyId: property.id,
            propertyName: property.name,
        })),
    ),
);

export const property = (propertyId: number): ReturnType<typeof createSelector> =>
    createSelector(propertiesSlice, (propertiesState): PaymentsProperty => propertiesState[propertyId] || {});

// Occupants
export const propertyOccupants = (propertyId: number): ReturnType<typeof createSelector> =>
    createSelector(propertiesSlice, (propertiesState): PaymentsOccupant[] => propertiesState[propertyId]?.occupants || []);

export const occupant = (propertyId: number, occupantId: number): ReturnType<typeof createSelector> =>
    createSelector(propertiesSlice, (propertiesState): PaymentsOccupant | undefined =>
        Object.prototype.hasOwnProperty.call(propertiesState, propertyId) &&
        propertiesState[propertyId].occupants.some((o) => o.id === occupantId)
            ? propertiesState[propertyId].occupants.filter((o) => o.id === occupantId)[0]
            : undefined,
    );
